/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// INTERFACES
import { MenuInterface, UserInterface } from "../../interfaces/User.interface";
import { flattenPermissions, PermissionTree } from "../../types/permissions";
import { FormAction } from "../../types/enums";

/**
 * State of the user slice: the user currently being viewed or edited,
 * the active selection, and the open/closed flags for this area's modals.
 */
export interface UserState {
  user: UserInterface;
  openModalCreateEditUser: boolean;
  openModalDeleteUser: boolean;
  /**
   * Which offline-credential call the TOTP prompt should make once the token
   * verifies: "get" describes the credential, "post" issues a new one.
   *
   * Slice state rather than a `setPswType` prop because the actions menu and
   * the modal surface that answers it are rendered by different screens now —
   * the wizard mounts them too, and a prop can only be threaded down one tree.
   */
  pswType: string;
  openModalResetUserPassword: boolean;
  openModalBlockUnBlockUser: boolean;
  openDrawerUser: boolean;
  userRefreshTable: boolean;
  permissions: MenuInterface;
  /**
   * Flat list of enabled tree_keys derived from `permissions`.
   * Computed once in updatePermissions for hasPermission lookups.
   * Stored as a plain string[] (not a Set) so the state stays serializable.
   */
  permissionSet: string[];
  openModalPsw: boolean;
  openModalOfflineCredential: boolean;
  formCreateEdit: FormAction;
}

const initialState: UserState = {
  user: {
    firstName: "",
    middleName: "",
    lastName: "",
    roleId: "",
    organizationId: "",
    email: "",
    contactInfo: [
      {
        id: "",
        address: "",
        phone: "",
        phone_code: "",
        priority: 0,
      },
    ],
  },
  openModalCreateEditUser: false,
  openModalDeleteUser: false,
  pswType: "",
  openModalResetUserPassword: false,
  openModalBlockUnBlockUser: false,
  openDrawerUser: false,
  userRefreshTable: false,
  permissions: {
    name: "",
    active: false,
    offline: false,
    pages: [],
  },
  permissionSet: [],
  openModalPsw: false,
  openModalOfflineCredential: false,
  formCreateEdit: FormAction.Create,
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    updateUser: (state, action: PayloadAction<UserInterface>) => {
      state.user = action.payload;
    },
    updateOpenModalCreateEditUser: (state, action: PayloadAction<boolean>) => {
      state.openModalCreateEditUser = action.payload;
    },
    updatePswType: (state, action: PayloadAction<string>) => {
      state.pswType = action.payload;
    },
    updateOpenModalDeleteUser: (state, action: PayloadAction<boolean>) => {
      state.openModalDeleteUser = action.payload;
    },
    updateOpenModalResetUserPassword: (state, action: PayloadAction<boolean>) => {
      state.openModalResetUserPassword = action.payload;
    },
    updateOpenModalBlockUnBlockUser: (state, action: PayloadAction<boolean>) => {
      state.openModalBlockUnBlockUser = action.payload;
    },
    updateOpenDrawerUser: (state, action: PayloadAction<boolean>) => {
      state.openDrawerUser = action.payload;
    },
    updateUserRefreshTable: (state, action: PayloadAction<boolean>) => {
      state.userRefreshTable = action.payload;
    },
    updatePermissions: (state, action: PayloadAction<MenuInterface>) => {
      state.permissions = action.payload;
      // The menu's flags are already effective: LiftCloud applies its default
      // rules (e.g. view-monitoring-dashboard granted to all) before rendering it.
      state.permissionSet = Array.from(
        flattenPermissions(action.payload as unknown as PermissionTree),
      );
    },
    updateOpenModalPsw: (state, action: PayloadAction<boolean>) => {
      state.openModalPsw = action.payload;
    },
    // Separate from openModalPsw because the two dialogs answer different
    // questions: one hands over a password that was just minted, the other
    // describes a credential that cannot be read back.
    updateOpenModalOfflineCredential: (state, action: PayloadAction<boolean>) => {
      state.openModalOfflineCredential = action.payload;
    },
    updateFormCreateEdit: (state, action: PayloadAction<FormAction>) => {
      state.formCreateEdit = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  updateUser,
  updateOpenModalCreateEditUser,
  updateOpenModalDeleteUser,
  updatePswType,
  updateOpenModalResetUserPassword,
  updateOpenModalBlockUnBlockUser,
  updateOpenDrawerUser,
  updateUserRefreshTable,
  updatePermissions,
  updateOpenModalPsw,
  updateOpenModalOfflineCredential,
  updateFormCreateEdit,
} = userSlice.actions;

export default userSlice.reducer;
